import { useNavigate } from 'react-router-dom';
import { useCartStore } from '../../../../storefront/lib/cart-store';
import { productToAddLine } from '../../../../storefront/lib/cart-map';
import { useBrand } from '../../../../storefront/lib/hooks';
import type { DisplayProduct } from '../../../../storefront/lib/types';

/**
 * Add-to-cart for the FLX landing's flavour cards and catalog tail.
 *
 * The landing sells, but it does NOT own the checkout: adding a line hands off
 * to the SHARED `/cart` → `/checkout` pages every other tenant uses. FLX briefly
 * had a bespoke cart drawer and a three-step checkout overlay living on the
 * landing; they were a second implementation of flows that already existed, and
 * they drifted (the overlay re-implemented sign-in, address capture and payment
 * selection). One checkout, maintained once.
 *
 * A variation-carrying product still can't be added blind — the server rejects a
 * line naming no variant (`variant_required`) and the public catalog carries no
 * variations — so those route to the product page to choose one, exactly as the
 * shared grid does.
 */
export function useAddToCart() {
  const addLine = useCartStore((s) => s.add);
  const brand = useBrand();
  const navigate = useNavigate();
  return (product: DisplayProduct) => {
    if (product.variants.length > 0) {
      navigate(`/product/${product.id}`);
      return;
    }
    addLine(productToAddLine(product, brand));
    navigate('/cart');
  };
}

/** `stockQty` is undefined on the public path and null when untracked — neither means zero. */
export function isSoldOut(p: DisplayProduct): boolean {
  return p.stockQty != null && p.stockQty <= 0;
}
